import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { FieldSet } from '../../shared/index.js';
import { FIELD_SETS, FIELD_SET_DESCRIPTIONS, FIELD_SET_LABELS } from '../../shared/index.js';

import { ApiError } from '../../api/client.js';
import type { BoardWithWorkflow } from '../../api/endpoints.js';
import { boardApi } from '../../api/endpoints.js';
import { queryKeys } from '../../app/query-client.js';
import { AudienceEditor, toBlocks, toRules } from '../../components/audience-editor.js';
import { HelpHint } from '../../components/help-hint.js';
import { TargetPicker } from '../../components/target-picker.js';
import { PageHeader } from '../../components/page-header.js';
import { Select } from '../../components/select.js';
import { Badge, Button, CONTROL_FIELD, CONTROL_SELECT, Spinner } from '../../components/ui.js';
import {
  useBoards,
  useDepartments,
  useDirectory,
  useJobRoles,
} from '../../hooks/use-directory.js';
import { ColumnsSection } from './board-columns-section.js';
import { MovesSection } from './board-moves-section.js';
import {
  BLANK_MOVE,
  BLANK_STAGE,
  BoardTitle,
  ConfirmDeleteBoard,
  INTAKE_HINT,
  RAISE_HINT,
  VIEW_HINT,
  slugify,
  useTargetLabel,
} from './board-setup-parts.js';
import type { MoveDraft, StageDraft } from './board-setup-parts.js';

export function BoardSetupPage(): ReactNode {
  const queryClient = useQueryClient();
  const { boards, isLoading } = useBoards(true);
  const { departments } = useDepartments();
  const { jobRoles } = useJobRoles();
  const people = useDirectory();
  const targetLabel = useTargetLabel();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [stageDraft, setStageDraft] = useState<StageDraft>(BLANK_STAGE);
  const [moveDraft, setMoveDraft] = useState<MoveDraft>(BLANK_MOVE);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardFieldSet, setNewBoardFieldSet] = useState<FieldSet>('basic');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [stageOrder, setStageOrder] = useState<readonly string[] | null>(null);

  const selected: BoardWithWorkflow | undefined =
    boards.find((entry) => entry.board.id === selectedId) ?? boards[0];

  const orderedStages = useMemo(() => {
    const stages = selected?.workflow.stages ?? [];

    if (stageOrder === null) return stages;

    const byId = new Map(stages.map((stage) => [stage.id, stage]));
    const ordered = stageOrder.flatMap((id) => {
      const stage = byId.get(id);
      return stage === undefined ? [] : [stage];
    });

    return ordered.length === stages.length ? ordered : stages;
  }, [selected, stageOrder]);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.boards.all });
  };

  const announced = useRef<string | null>(null);

  useEffect(() => {
    if (selected === undefined) {
      announced.current = null;
      return;
    }

    const signature = `${selected.board.id}|${selected.problems.map((issue) => issue.code).join(',')}`;

    if (signature === announced.current) return;

    announced.current = signature;

    for (const issue of selected.problems) {
      if (issue.severity === 'error') toast.error(issue.message);
      else toast.warn(issue.message);
    }
  }, [selected]);

  const fail = (fallback: string) => (error: unknown) => {
    toast.error(error instanceof ApiError ? error.message : fallback);
  };

  const createBoard = useMutation({
    mutationFn: () =>
      boardApi.create({
        slug: slugify(newBoardName),
        name: newBoardName.trim(),
        description: null,
        fieldSet: newBoardFieldSet,
        viewAudience: [],
        raiseAudience: [],
        intakeTarget: null,
        assigneeMayBeCreator: true,
        requiresProjectAuthorisation: false,
        allowManualStatusChange: true,
      }),
    onSuccess: async (created) => {
      setNewBoardName('');
      setSelectedId(created.board.id);
      await refresh();
    },
    onError: fail('The board could not be created.'),
  });

  const updateBoard = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      boardApi.update(id, patch),
    onSuccess: async () => {
      await refresh();
    },
    onError: fail('That board setting could not be saved.'),
  });

  const reorderStages = useMutation({
    mutationFn: ({ boardId, stageIds }: { boardId: string; stageIds: readonly string[] }) =>
      boardApi.reorderStages(boardId, stageIds),
    onSuccess: async () => {
      await refresh();
      setStageOrder(null);
    },
    onError: (error: unknown) => {
      setStageOrder(null);
      fail('The columns could not be reordered.')(error);
    },
  });

  const removeBoard = useMutation({
    mutationFn: (id: string) => boardApi.remove(id),
    onSuccess: async () => {
      setSelectedId(null);
      setConfirmingDelete(false);
      await refresh();
    },
    onError: (error: unknown) => {
      setConfirmingDelete(false);
      fail('The board could not be deleted.')(error);
    },
  });

  const addStage = useMutation({
    mutationFn: (boardId: string) =>
      boardApi.createStage(boardId, {
        slug: slugify(stageDraft.label),
        label: stageDraft.label.trim(),
        tone: stageDraft.tone,
        resultingStatus: stageDraft.resultingStatus,
        isTerminal: stageDraft.isTerminal,
        afterStageId: stageDraft.afterStageId === '' ? null : stageDraft.afterStageId,
      }),
    onSuccess: async () => {
      setStageDraft(BLANK_STAGE);
      await refresh();
    },
    onError: fail('The column could not be added.'),
  });

  const editStage = useMutation({
    mutationFn: ({
      boardId,
      stageId,
      patch,
    }: {
      boardId: string;
      stageId: string;
      patch: Record<string, unknown>;
    }) => boardApi.updateStage(boardId, stageId, patch),
    onSuccess: async () => {
      await refresh();
    },
    onError: fail('The column could not be changed.'),
  });

  const dropStage = useMutation({
    mutationFn: ({ boardId, stageId }: { boardId: string; stageId: string }) =>
      boardApi.removeStage(boardId, stageId),
    onSuccess: async () => {
      await refresh();
    },
    onError: fail('The column could not be deleted.'),
  });

  const addMove = useMutation({
    mutationFn: (boardId: string) =>
      boardApi.createTransition(boardId, {
        slug: slugify(moveDraft.label),
        label: moveDraft.label.trim(),
        fromStageId: moveDraft.fromStageId,
        toStageId: moveDraft.toStageId,
        actorRule: moveDraft.actorRule,
        actorDepartmentId: moveDraft.actorDepartmentId === '' ? null : moveDraft.actorDepartmentId,
        forbidSelfService: moveDraft.forbidSelfService,
        handOffTarget: moveDraft.handOffTarget,
      }),
    onSuccess: async () => {
      setMoveDraft(BLANK_MOVE);
      await refresh();
    },
    onError: fail('The move could not be added.'),
  });

  const dropMove = useMutation({
    mutationFn: ({ boardId, transitionId }: { boardId: string; transitionId: string }) =>
      boardApi.removeTransition(boardId, transitionId),
    onSuccess: async () => {
      await refresh();
    },
    onError: fail('The move could not be deleted.'),
  });

  if (isLoading) {
    return (
      <div className="grid flex-1 place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Board setup" subtitle="Columns, moves and who is allowed to make them." />

      <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
        <nav className="shrink-0 overflow-y-auto border-b border-hairline px-4 py-4 lg:w-[240px] lg:border-r lg:border-b-0 lg:py-5 dark:border-dark-line">
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar lg:flex-col lg:gap-0 lg:overflow-visible lg:pb-0">
            {boards.map((entry) => (
              <button
                key={entry.board.id}
                type="button"
                onClick={() => {
                  setSelectedId(entry.board.id);
                }}
                className={`flex shrink-0 items-center gap-2 rounded-field px-3 py-2 text-left text-[13.5px] font-semibold transition lg:mb-1 lg:w-full ${
                  selected?.board.id === entry.board.id
                    ? 'bg-navy text-white'
                    : 'text-navy hover:bg-navy/5 dark:text-dark-ink dark:hover:bg-white/5'
                }`}
              >
                <span className="truncate">{entry.board.name}</span>
                {entry.problems.some((issue) => issue.severity === 'warning') ? (
                  <span className="size-1.5 rounded-full bg-caution lg:ml-auto" />
                ) : null}
              </button>
            ))}
          </div>

          <div className="mt-4 border-t border-hairline pt-4 lg:mt-5 dark:border-dark-line">
            <label className="field-label" htmlFor="new-board">
              New board
            </label>
            <input
              id="new-board"
              value={newBoardName}
              onChange={(event) => {
                setNewBoardName(event.target.value);
              }}
              placeholder="IT requests"
              className={`${CONTROL_FIELD} mb-2`}
            />
            <Select
              label="Field set"
              value={newBoardFieldSet}
              onChange={(next) => {
                setNewBoardFieldSet(next as FieldSet);
              }}
              options={FIELD_SETS.map((fieldSet) => ({
                value: fieldSet,
                label: FIELD_SET_LABELS[fieldSet],
              }))}
              className={`${CONTROL_SELECT} mb-2`}
            />
            <p className="mb-2 text-[11.5px] leading-snug text-muted">
              {FIELD_SET_DESCRIPTIONS[newBoardFieldSet]}
            </p>
            <Button
              size="sm"
              className="w-full"
              disabled={newBoardName.trim().length < 2}
              loading={createBoard.isPending}
              onClick={() => {
                createBoard.mutate();
              }}
            >
              Create
            </Button>
          </div>
        </nav>

        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
          {selected === undefined ? (
            <p className="py-16 text-center text-[14px] text-muted">No boards yet.</p>
          ) : (
            <>
              <section className="surface-card mb-5 p-5 dark:border-dark-line dark:bg-dark-panel">
                <div className="mb-4 flex items-center gap-2">
                  <BoardTitle
                    key={selected.board.id}
                    board={selected.board}
                    busy={updateBoard.isPending}
                    onRename={(name) => {
                      updateBoard.mutate({ id: selected.board.id, patch: { name } });
                    }}
                  />
                  <Badge tone="neutral">{FIELD_SET_LABELS[selected.board.fieldSet]}</Badge>
                  {selected.board.isSystem ? <Badge tone="gold">Built in</Badge> : null}
                  {selected.board.isActive ? null : <Badge tone="caution">Archived</Badge>}

                  {selected.board.isSystem ? null : (
                    <Button
                      variant="danger"
                      size="sm"
                      className="ml-auto"
                      onClick={() => {
                        setConfirmingDelete(true);
                      }}
                    >
                      Delete board
                    </Button>
                  )}
                </div>

                <div className="flex flex-col gap-5">
                  <div>
                    <div className="mb-2 flex items-center gap-1.5">
                      <span className="field-label mb-0">Who may view this board</span>
                      <HelpHint title="Who may view this board">{VIEW_HINT}</HelpHint>
                    </div>
                    <AudienceEditor
                      blocks={toBlocks(selected.board.viewAudience, jobRoles, people)}
                      departments={departments}
                      jobRoles={jobRoles}
                      people={people}
                      emptyLabel="Everyone signed in can open it."
                      onChange={(next) => {
                        updateBoard.mutate({
                          id: selected.board.id,
                          patch: { viewAudience: toRules(next) },
                        });
                      }}
                    />
                  </div>

                  <div>
                    <div className="mb-2 flex items-center gap-1.5">
                      <span className="field-label mb-0">Who may raise a request</span>
                      <HelpHint title="Who may raise a request">{RAISE_HINT}</HelpHint>
                    </div>
                    <AudienceEditor
                      blocks={toBlocks(selected.board.raiseAudience, jobRoles, people)}
                      departments={departments}
                      jobRoles={jobRoles}
                      people={people}
                      emptyLabel="Everyone signed in can raise one."
                      onChange={(next) => {
                        updateBoard.mutate({
                          id: selected.board.id,
                          patch: { raiseAudience: toRules(next) },
                        });
                      }}
                    />
                  </div>

                  <div>
                    <div className="mb-2 flex items-center gap-1.5">
                      <span className="field-label mb-0">Where a new request lands</span>
                      <HelpHint title="Where a new request lands">{INTAKE_HINT}</HelpHint>
                    </div>
                    <TargetPicker
                      value={selected.board.intakeTarget}
                      departments={departments}
                      jobRoles={jobRoles}
                      people={people}
                      nowhereLabel="The requester picks a department"
                      onChange={(next) => {
                        updateBoard.mutate({
                          id: selected.board.id,
                          patch: { intakeTarget: next },
                        });
                      }}
                    />
                  </div>
                </div>
              </section>

              <ColumnsSection
                boardId={selected.board.id}
                orderedStages={orderedStages}
                allStages={selected.workflow.stages}
                stageDraft={stageDraft}
                onStageDraftChange={setStageDraft}
                onReorder={(next) => {
                  setStageOrder(next);
                  reorderStages.mutate({ boardId: selected.board.id, stageIds: next });
                }}
                addStage={addStage}
                editStage={editStage}
                dropStage={dropStage}
              />

              <MovesSection
                boardId={selected.board.id}
                stages={selected.workflow.stages}
                transitions={selected.workflow.transitions}
                moveDraft={moveDraft}
                onMoveDraftChange={setMoveDraft}
                departments={departments}
                jobRoles={jobRoles}
                people={people}
                targetLabel={targetLabel}
                addMove={addMove}
                dropMove={dropMove}
              />
            </>
          )}
        </div>
      </div>

      {confirmingDelete && selected !== undefined ? (
        <ConfirmDeleteBoard
          name={selected.board.name}
          ticketCount={selected.ticketCount}
          liveTicketCount={selected.liveTicketCount}
          pending={removeBoard.isPending}
          onCancel={() => {
            setConfirmingDelete(false);
          }}
          onConfirm={() => {
            removeBoard.mutate(selected.board.id);
          }}
        />
      ) : null}
    </>
  );
}
